import {
  Button,
  CartItem,
  DateInput,
  FormField,
  getErrors,
  PageHeader,
  showToast,
  Typography,
} from '@maia/ui';
import { createFileRoute } from '@tanstack/react-router';
import { getCustomerOptions } from '../../../../../features/customer/customer/queryOptions';
import { CustomerInformation } from '../../../../../features/shared/customer/customer-information';
import { mapper } from '@maia/icons';
import { useCreateClaim } from '../../../../../features/claim/claim/create-claim.options';
import { getInsuranceOptions } from '../../../../../features/insurance/insurance/queryOptions';
import {
  CalendarDate,
  DateValue,
  getLocalTimeZone,
} from '@internationalized/date';
import { getTodayInCalendarDate } from '@maia/utils';
import * as v from 'valibot';
import { useAppForm } from '../../../../../features/form/dynamic-form/dynamic-form';
import { format } from 'date-fns';

export const Route = createFileRoute(
  '/_authenticated/claims/new/_layout/$customer/$insurance',
)({
  component: RouteComponent,
  loader: async ({
    context: { queryClient },
    params: { customer: customerGuid, insurance: insuranceGuid },
  }) => {
    const insurance = await queryClient.ensureQueryData(
      getInsuranceOptions(insuranceGuid),
    );

    const customer = await queryClient.ensureQueryData(
      getCustomerOptions(customerGuid),
    );

    return { insurance, customer };
  },
});

function RouteComponent() {
  const data = Route.useLoaderData();
  const today = getTodayInCalendarDate();

  const navigate = Route.useNavigate();

  const { mutateAsync: createClaim, isPending } = useCreateClaim(
    data.customer.guid,
    async (response) => {
      if (response.type === 'ems') {
        const { ems_url, ems_jwt } = response;

        const form = document.createElement('form');
        form.method = 'POST';
        form.action = ems_url;

        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'JWT';
        input.value = ems_jwt;
        form.appendChild(input);

        document.body.appendChild(form);

        form.target = '_blank';
        form.submit();

        document.body.removeChild(form);

        await navigate({
          to: '/claims/new/$customer/external-system',
          params: { customer: data.customer.guid },
        });
      } else {
        await navigate({
          to: '/claims/new/$customer/success',
          params: { customer: data.customer.guid },
        });
      }
    },
    () => {
      showToast({
        intent: 'error',
        title: 'Er ging iets mis',
        description:
          'We konden niet de claim voor je aanmaken. Probeer het later opnieuw',
        config: { position: 'top-right' },
      });
    },
  );

  const form = useAppForm({
    defaultValues: {
      date: today,
      description: '',
    } as {
      date: DateValue | null;
      description: string;
    },
    onSubmit: async ({ value }) => {
      if (value?.date) {
        const dateObject = value.date.toDate(getLocalTimeZone());
        const formattedDate = format(dateObject, 'yyyy-MM-dd');

        await createClaim({
          productGuid: data.insurance.product_guid,
          claim_date: formattedDate,
          subject: value.description,
        });
      }
    },
  });

  return (
    <>
      <PageHeader
        className="area-page-header"
        title="Korte omschrijving"
        backLink={{
          to: '/claims/new/$customer',
          params: { customer: data.customer.guid },
        }}
      />

      <div className="area-page-content content-grid">
        <div className="col-span-full">
          <form
            id="more-information-form"
            onSubmit={(event) => {
              event.preventDefault();
              event.stopPropagation();
              form.handleSubmit();
            }}
            className="grid grid-cols-12 gap-24 col-span-full"
          >
            <div className="col-span-full">
              <form.AppField
                name="date"
                validators={{
                  onChange: v.pipe(
                    v.instance(CalendarDate, 'Datum is verplicht'),
                    v.custom((date: unknown) => {
                      if (date instanceof CalendarDate) {
                        return date.year > 1900 && today.compare(date) >= 0;
                      }

                      return false;
                    }, 'Datum is niet geldig'),
                  ),
                }}
              >
                {(field) => (
                  <FormField
                    label="Wanneer vond het plaats?"
                    className="col-span-full"
                    error={getErrors(field)}
                    hint={`De ingangsdatum van de verzekering is ${format(data.insurance.effectiveDate, 'dd-MM-yyyy')}`}
                    inputSlot={
                      <DateInput
                        fieldSize="large"
                        defaultValue={today}
                        id={field.name}
                        name={field.name}
                        value={field.state.value}
                        onBlur={field.handleBlur}
                        onChange={(date) => field.handleChange(date)}
                        includePicker
                      />
                    }
                  />
                )}
              </form.AppField>
            </div>
            <div className="col-span-full">
              <form.AppField
                name="description"
                validators={{
                  onChange: v.pipe(
                    v.string('Beschrijving is verplicht'),
                    v.trim(),
                    v.minLength(1, 'Beschrijving is verplicht'),
                  ),
                  onMount: v.pipe(
                    v.string('Beschrijving is verplicht'),
                    v.trim(),
                    v.minLength(1, 'Beschrijving is verplicht'),
                  ),
                }}
              >
                {(field) => {
                  return (
                    <field.TextareaField
                      label="Wat is er gebeurd in het kort?"
                      error={getErrors(field)}
                    />
                  );
                }}
              </form.AppField>
            </div>
          </form>
        </div>
      </div>

      <div className="flex flex-col area-page-sidebar gap-y-32">
        <Typography
          variant="display6"
          weight="medium"
          className="text-primaryMain"
        >
          Gekozen details
        </Typography>

        {data.customer && <CustomerInformation customer={data.customer} />}

        <CartItem
          icon={mapper(data.insurance.icon)}
          name={data.insurance.title}
          specs={data.insurance.specs
            .filter((spec) => !!spec.value)
            .map((spec) => ({
              icon: spec.icon,
              title: spec.label,
              description: spec.value,
            }))}
          billingUnit={data.customer.billingUnit}
        />

        <div className="flex flex-col mt-auto gap-y-12">
          <form.Subscribe
            selector={(state) => [
              state.canSubmit,
              state.isSubmitting,
              state.isValid,
            ]}
          >
            {([canSubmit, isSubmitting, isValid]) => (
              <Button
                form="more-information-form"
                type="submit"
                intent="cta"
                size="large"
                isDisabled={!isValid || !canSubmit || isSubmitting}
                isLoading={isPending}
                className="w-full justify-center"
              >
                Afronden
              </Button>
            )}
          </form.Subscribe>
        </div>
      </div>
    </>
  );
}
